/**
 * Shared fetch-effect hook for analytics-v2 widgets (CORE-1041).
 *
 * Encapsulates the fetch loop every widget needs:
 *   - Fetch on mount and on `deps` change.
 *   - Skip when the parent tab is hidden (tabVisible from RefreshContext).
 *   - Refetch when the dashboard-wide "Refresh now" button is pressed
 *     (manualRefreshNonce from RefreshContext).
 *   - Cancellation guard: unmount-during-fetch and rapid dep-change do
 *     not stomp the latest data with a stale response.
 *   - Auto-poll deliberately omitted — marketing-lane widgets pull
 *     daily-refreshed data (plan §10), so a 60s poll would be wasted
 *     requests. Freshness comes from window/source changes and manual
 *     refresh.
 *
 * Errors are formatted via `formatErrorMessage` so the caller can render
 * the returned string directly in a user-facing error banner.
 */

import { useEffect, useState } from 'react';
import { useRefresh } from '@/context/RefreshContext';
import { formatErrorMessage } from '@/utils/errors';

export interface AnalyticsV2FetchResult<T> {
  data: T | null;
  error: string | null;
  isLoading: boolean;
}

/**
 * @param fetcher A function that returns a Promise of the response. Invoked
 *   inside a `useEffect` whose dependency array is `[...deps,
 *   manualRefreshNonce, tabVisible]`.
 *
 * ⚠️ **Every piece of state read inside `fetcher` MUST appear in `deps`.**
 * The hook disables react-hooks/exhaustive-deps at the effect call site (to
 * avoid an "array spread is not exhaustive" false positive), which means the
 * linter cannot warn the caller about stale closures. If you capture
 * `selectedWindow` and `source` inside `fetcher`, both must be in `deps`.
 * Forgetting one causes silent stale refetches — the fetch always runs but
 * returns data for the previous value of the omitted state.
 *
 * @param deps Additional dependencies beyond the built-in
 *   `manualRefreshNonce` and `tabVisible` from RefreshContext. Pass every
 *   value that `fetcher` reads from the enclosing scope.
 */
export function useAnalyticsV2Fetch<T>(
  fetcher: () => Promise<T>,
  deps: React.DependencyList,
): AnalyticsV2FetchResult<T> {
  const { manualRefreshNonce, tabVisible } = useRefresh();

  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!tabVisible) return undefined;

    let cancelled = false;
    // `data` is deliberately NOT reset here — keeping the previous
    // response visible during a refetch enables "refresh over stale"
    // UX (dim the existing table while the new data loads) which is a
    // common table pattern. Widgets that want a hard "blank + reload"
    // gate their body render on `!isLoading` (as the shell does), so
    // the loading state naturally takes precedence in that render
    // cycle regardless of `data`'s value.
    setIsLoading(true);
    setError(null);

    void fetcher()
      .then((response) => {
        if (!cancelled) setData(response);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(formatErrorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, manualRefreshNonce, tabVisible]);

  return { data, error, isLoading };
}
